import { useEffect, useRef, useState } from "react";
import "./PropertyPostView.scss";
import clsx from "clsx";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Modal, TextField } from "@mui/material";
import toast from "react-hot-toast";
import CoAgentPropertyPostActionMenuIcon from "./CoAgentPropertyPostActionMenuIcon";
import { PropertyApi } from "../../../../../utils/api/Property";
import { CommonEnums } from "../../../../../enums/common.enums";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import CustomPageLoader from "../../../../../components/loaders/CustomPageLoader";
import ProtectedComponent from "../../../../../components/ProtectedComponent";
import NavigationLayout from "../../../navigation";
import { clientRoutes } from "../../../../../utils/routes";
import BackArrowIcon from "../../../../../components/icons/BackArrowIcon";
import Loading from "../../../../loading/Loading";
import { getImageUrl } from "../../../../../utils/helpers/common";
import LeftArrowIcon from "../../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../../components/icons/RightArrowIcon";
import PreviewModal from "../../propertypost/view/PreviewModal";

interface PropertyItem {
  _id: string;
  property_no?: string;
  property_no_and_street_name?: string;
  city?: string;
  state?: string;
  post_code?: string;
  thumbnail_image_ref?: { compressed_file_path?: string };
  agent_ref?: {
    profile_image_ref?: { compressed_file_path?: string };
    principal_agent_first_name?: string;
    principal_agent_last_name?: string;
    principal_phone_country_code?: string;
    principal_agent_mobile_number?: string;
  };
  property_type_ref?: { name?: string };
  listing_type?: { name?: string };
  property_stage_ref?: { name?: string };
  price_type?: { slug?: string };
  price?: string;
  start_price?: string;
  end_price?: string;
  eoi_datetime?: string;
  createdAt?: string;
}

interface PropertySetupPostProps {
  property?: PropertyItem;
}

const propertyStages = [
  "Coming Soon",
  "Coming Soon for Lease",
  "Available Soon",
  "Pre-Leasing Now",
  "Coming Soon for Rent",
  "Pre-Lease Now",
  "Rental Coming Soon",
];

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const CoAgentPropertyView = () => {
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("Facebook");
  const [sameTextToggle, setSameTextToggle] = useState(false);
  const [otherImagesIndex, setOtherImagesIndex] = useState(0);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [postPreviewIndex, setPostPreviewIndex] = useState(0);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  // const [date, setDate] = useState("");
  // const [timeTo, setTimeTo] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [date, setDate] = useState<string>("");
  const [time, setTime] = useState<string>("");
  const navigate = useNavigate();
  const [propertyPost, setPropertyPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { propertyPostId } = useParams<{ propertyPostId: string }>();
  const [isUpdateTemplateOpen, setIsUpdateTemplateOpen] = useState(false);
  const scheduleFormRef = useRef<HTMLDivElement | null>(null);
  const [scheduleDateTime, setScheduleDateTime] = useState<dayjs.Dayjs | null>(
    null
  );
  const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
  const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [isPolling, setIsPolling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [updating, setUpdating] = useState(false);

  const tabs = [
    "Facebook",
    "Instagram",
    "Google Business",
    "Linkedin",
    "Youtube",
  ];

  const images = [
    "/images/property-inside.jpg",
    "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
    "https://as2.ftcdn.net/jpg/02/61/89/29/1000_F_261892957_6jyBXvEgM79iYr1eEiJKCosnVPJdvHHr.jpg",
  ];

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleShowScheduleForm = () => {
    setShowScheduleForm(true);

    setTimeout(() => {
      scheduleFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const formatPostType = (type?: string) => {
    if (!type) return "N/A";
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleOtherImagesPrev = () => {
    setOtherImagesIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleOtherImagesNext = () => {
    setOtherImagesIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handlePostPreviewPrev = () => {
    setPostPreviewIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handlePostPreviewNext = () => {
    setPostPreviewIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const getListingType = (post: any): string | undefined => {
    if (post.image_post_design_activity_ref) {
      return post.image_post_design_activity_ref.listing_type;
    }
    if (post.story_post_design_activity_ref) {
      return post.story_post_design_activity_ref.listing_type;
    }
    if (post.video_post_design_activity_ref) {
      return post.video_post_design_activity_ref.listing_type;
    }
    if (post.reel_post_design_activity_ref) {
      return post.reel_post_design_activity_ref.listing_type;
    }
    return undefined;
  };

  const fullText = `Featuring: 5 🛌  Bedrooms | 5 🛀  Bathrooms | 2 🚗 Car Spaces... more
💸 Price Range: $1,675,000 - $1,842,000
Discover a lifestyle that's more than extraordinary – it's truly exceptional. Nestled in the heart of Berwick Waters, this stunning property at 6 Rockbank Road offers everything you need to live like a star.
Don't miss the chance to experience the allure of celebrity living. Come and see what makes 6 Rockbank Road the ultimate address for those who demand the best.
Your dream lifestyle awaits!
📸 Explore More: https://www.realestate.com.au/property-house-vic-clyde...
For more information or to book a viewing,

Lern more at BrandFeeds [Brand feeds Link for this property here]

Contact
Sukhdeep Singh 📲 0420 879 430
Mohan Singh 📲 0426 250 579
#celebrityliving #dreamhome #luxurylifestyle #clydenorthrealestate 
#berwickwaters #melbournerealestate #homesforsale #luxuryhomes 
#realestategoals #propertyforsale #dreamliving #familyhome 
#investmentproperty`;

  const shortText = `Experience Celebrity Living at 📍 6 Rockbank Road, Clyde North Spaces...`;

  const fetchPropertyPost = async () => {
    try {
      setLoading(true);
      const res = await PropertyApi.getPropertyPostById(
        propertyPostId as string
      );

      if (res?.data?.success) {
        const post = res.data.data;

        if (post.schedule_datetime) {
          const dt = dayjs(post.schedule_datetime);
          setScheduleDateTime(dt);
          setScheduleDate(dt);
          setScheduleTime(dt);
        }

        setPropertyPost(post);

        const templateFilters = {
          template_type_id: post.template_type,
          subscription_type: post.subscription_type,
          post_type: post.post_type,
          listing_type: getListingType(post),
        };

        console.log("Template filters to send:", templateFilters);
        // await fetchTemplates(templateFilters);
      } else {
        setError("Failed to load property post");
      }
    } catch (err) {
      setError("Something went wrong while fetching");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyPostId) fetchPropertyPost();
  }, [propertyPostId]);

  useEffect(() => {
    if (propertyPost?.schedule_datetime) {
      setScheduleDateTime(dayjs(propertyPost.schedule_datetime));
      setShowScheduleForm(true);
    }
  }, [propertyPost]);

  useEffect(() => {
    if (propertyPost?._id) {
      const currentParams: Record<string, string> = {};

      searchParams.forEach((value, key) => {
        currentParams[key] = value;
      });

      setSearchParams({
        ...currentParams,
        post_type: propertyPost.post_type || "",
        // subscription_type: propertyPost.subscription_type || "",
      });
    }
  }, [propertyPost, searchParams, setSearchParams]);

  const property = propertyPost?.property_id;

  const imageUrl = property?.thumbnail_image_ref
    ? `${BASE_URL}/${property?.thumbnail_image_ref?.compressed_file_path}`
    : "/images/no-image.png";

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "completed";
      case "processing":
        return "processing";
      case "scheduled for post":
        return "scheduled";
      case "pending_for_social_media_upload":
        return "pending-upload";
      case "cancelled":
        return "cancelled";
      case "errored":
      case "error":
        return "errored";
      default:
        return "";
    }
  };

  const handleUpdate = async (isScheduled: boolean) => {
    if (!propertyPostId) return;

    const payload: any = {};

    if (isScheduled) {
      if (!scheduleDate || !scheduleTime) {
        toast.error("Please select both date and time");
        return;
      }

      const combined = scheduleDate
        .hour(scheduleTime.hour())
        .minute(scheduleTime.minute())
        .second(0)
        .millisecond(0);

      payload.schedule_datetime = combined.toISOString();
    }

    setUpdating(true);
    try {
      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);
      if (res?.data?.success) {
        toast.success("Post updated successfully!");
        setPropertyPost((prev: any) => ({ ...prev, ...payload }));
        setShowScheduleForm(false);
      } else {
        toast.error(res?.data?.message || "Failed to update post");
      }
    } catch (err) {
      console.error("Update failed:", err);
      toast.error("Something went wrong while updating");
    } finally {
      setUpdating(false);
    }
  };

  const getSelectedTemplateId = () => {
    if (!propertyPost) return "";

    switch (propertyPost.post_type) {
      case CommonEnums.postTypes.image_post:
        return (
          propertyPost?.image_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      case CommonEnums.postTypes.story_post:
        return (
          propertyPost?.story_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      case CommonEnums.postTypes.video_post:
        return (
          propertyPost?.video_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      case CommonEnums.postTypes.reel_post:
        return (
          propertyPost?.reel_post_design_activity_ref?.design_template_id ||
          propertyPost?.template_id ||
          ""
        );
      default:
        return propertyPost?.template_id || "";
    }
  };

  const statusFromAPI = propertyPost?.post_status || "";

  const renderDesignImage = (item: any) => {
    const designActivityRef = item?._id ? item : null;

    const assetUrlRaw = designActivityRef?.design_task_id?.output_image_path;
    const postType =
      designActivityRef?.design_post_type || item?.design_post_type;
    const designTaskStatus = designActivityRef?.design_task_id?.task_status;

    const isLoading = designTaskStatus === CommonEnums.taskStatus.queued;
    const isErrored = designTaskStatus === CommonEnums.taskStatus.errored;

    const assetUrl = assetUrlRaw
      ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
      : null;

    const openPreview = () => {
      setPreviewImages([
        assetUrl ||
          "/images/image-video-generation-error-fallback-image-for-card.png",
      ]);
      setPreviewIndex(0);
      setIsPreviewOpen(true);
    };

    // --- Error State ---
    if (isErrored) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error Preview"
            className="activity-thumbnail placeholder-image"
          />
        </div>
      );
    }

    if (isLoading) {
      const fallback =
        postType === CommonEnums.postType.video_post.key
          ? "/images/square-fallback-image-for-video-preview.png"
          : "/images/square-fallback-image-for-image-preview.png";

      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src={fallback}
            alt="Loading Preview"
            className="activity-thumbnail placeholder-image"
          />
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    if (postType === CommonEnums.postType.story_post.key) {
      return (
        <div className="activity-image-section story-post-image-container">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Story Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.video_post.key) {
      return (
        <div className="activity-image-section video-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Video Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    if (postType === CommonEnums.postType.reel_post.key) {
      return (
        <div className="activity-image-section reel-placeholder-collection">
          <img
            src={
              assetUrl ||
              "/images/image-video-generation-error-fallback-image-for-card.png"
            }
            alt="Reel Preview"
            className="activity-thumbnail"
            onError={(e: any) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "/images/image-video-generation-error-fallback-image-for-card.png";
            }}
          />
        </div>
      );
    }

    return (
      <div className="activity-image-section">
        <img
          src={
            assetUrl ||
            "/images/image-video-generation-error-fallback-image-for-card.png"
          }
          alt="Image Preview"
          className="activity-thumbnail"
          onError={(e: any) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              "/images/image-video-generation-error-fallback-image-for-card.png";
          }}
        />
      </div>
    );
  };

  const designStatuses = [
    propertyPost?.image_post_design_activity_ref?.design_status,
    propertyPost?.story_post_design_activity_ref?.design_status,
    propertyPost?.video_post_design_activity_ref?.design_status,
    propertyPost?.reel_post_design_activity_ref?.design_status,
  ].filter(Boolean);

  const hasPendingForUpload = designStatuses.includes(
    "pending_for_social_media_upload"
  );

  function formatPostStatus(status?: string): string {
    if (!status) return "-";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const getPostStatusDisplay = (propertyPost: any) => {
    if (propertyPost?.post_status === "cancelled") {
      return "Cancelled";
    }

    const designActivityRef =
      propertyPost?.image_post_design_activity_ref ||
      propertyPost?.story_post_design_activity_ref ||
      propertyPost?.video_post_design_activity_ref ||
      propertyPost?.reel_post_design_activity_ref;

    const scheduleDatetime = propertyPost?.schedule_datetime
      ? propertyPost.schedule_datetime.$date
        ? new Date(propertyPost.schedule_datetime.$date)
        : new Date(propertyPost.schedule_datetime)
      : null;

    if (scheduleDatetime) {
      return "Scheduled to Post";
    }

    if (!designActivityRef) {
      return formatPostStatus(propertyPost?.post_status || "-");
    }

    const designStatus = designActivityRef.design_status;

    if (designStatus === "pending_for_social_media_upload") {
      return "Pending Upload";
    } else if (designStatus === "processing") {
      return "Processing...";
    }

    return formatPostStatus(propertyPost?.post_status || "-");
  };

  const startPolling = (interval = 3000) => {
    if (isPolling) return;
    setIsPolling(true);

    const postTypeFromUrl =
      searchParams.get("post_type") || propertyPost?.post_type;

    const postTypeMap: Record<string, string> = {
      image_post: "image_post_design_activity_ref",
      story_post: "story_post_design_activity_ref",
      video_post: "video_post_design_activity_ref",
      reel_post: "reel_post_design_activity_ref",
    };

    const designKey = postTypeMap[postTypeFromUrl || ""] || "";

    if (!designKey) {
      console.warn("Unknown post_type:", postTypeFromUrl);
      setIsPolling(false);
      return;
    }

    const timer = setInterval(async () => {
      if (!propertyPostId) return;

      try {
        const res = await PropertyApi.getPropertyPostById(propertyPostId);
        if (res?.data?.success) {
          const post = res.data.data;

          setPropertyPost((prev: any) => {
            if (
              !prev ||
              prev?.[designKey]?.design_status !==
                post?.[designKey]?.design_status
            ) {
              return post;
            }
            return prev;
          });

          const designStatus = post?.[designKey]?.design_status;

          if (designStatus === "pending_for_social_media_upload") {
            clearInterval(timer);
            setIsPolling(false);
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, interval);
  };

  useEffect(() => {
    if (propertyPost?._id && !isPolling) {
      startPolling(3000);
    }
  }, [propertyPost]);

  const getPreviewImages = (propertyPost: any): string[] => {
    if (!propertyPost) return [];

    const refs = [
      propertyPost.image_post_design_activity_ref,
      propertyPost.story_post_design_activity_ref,
      propertyPost.video_post_design_activity_ref,
      propertyPost.reel_post_design_activity_ref,
    ].filter(Boolean);

    return refs
      .map((ref: any) => {
        if (ref?.design_task_id?.output_image_path) {
          try {
            return CommonHelpers.getImageUrlFromImageTool({
              path: ref.design_task_id.output_image_path,
            });
          } catch (error) {
            console.error("Error generating image URL:", error);
            return null;
          }
        }

        if (ref?.output_image_path) {
          try {
            return CommonHelpers.getImageUrlFromImageTool({
              path: ref.output_image_path,
            });
          } catch (error) {
            console.error("Error generating image URL:", error);
            return null;
          }
        }

        return null;
      })
      .filter(Boolean) as string[];
  };

  const formattedSchedule = propertyPost?.schedule_datetime
    ? dayjs(propertyPost?.schedule_datetime).format(
        "MMMM DD, YYYY [at] hh:mm A"
      )
    : "-";

  const handlePreviewOpen = () => {
    const designRefs = [
      propertyPost?.image_post_design_activity_ref,
      propertyPost?.story_post_design_activity_ref,
      propertyPost?.video_post_design_activity_ref,
      propertyPost?.reel_post_design_activity_ref,
    ].filter(Boolean);

    const images = designRefs.map((ref) => {
      const path =
        ref?.design_task_id?.output_image_path ||
        ref?.output_image_path ||
        ref?.design_task_id?.output_file_path;
      return path
        ? CommonHelpers.getImageUrlFromImageTool({ path })
        : "/images/image-video-generation-error-fallback-image-for-card.png";
    });

    setPreviewImages(images);
    setPreviewIndex(0);
    setIsPreviewOpen(true);
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="setup-property-post">
          <div
            className="propertie-view-back-btn"
            onClick={() => {
              const subscriptionType = searchParams.get("subscription_type");
              const backUrl = subscriptionType
                ? `${clientRoutes.AgentPropertyPostList}?subscription_type=${subscriptionType}`
                : clientRoutes.AgentPropertyPostList;

              navigate(backUrl);
            }}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>

            <p className="bakc-text-contain">Back to Property Post List</p>
          </div>
          <div className="header-row">
            <h1 className="title">Property Post</h1>
            <div className="action-menu-icon-wrapper">
              <CoAgentPropertyPostActionMenuIcon
                postId={propertyPost?._id}
                onUpdated={fetchPropertyPost}
                previewImageUrl={getPreviewImages(propertyPost)[0] || ""}
                onPreview={handlePreviewOpen}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <Loading />
            </div>
          ) : (
            <>
              <div className="auction-top-info">
                <div className="property-post-view-label">
                  Property Post from Agent
                </div>
                <div className="top-row">
                  <div className="info-item">
                    <label>Property Post Id</label>
                    <p>{propertyPost?._id}</p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <p
                      className={`status-badge ${getStatusClass(
                        statusFromAPI
                      )}`}
                    >
                      {getPostStatusDisplay(propertyPost)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Agent</label>
                    <p className="agent-name">
                      {propertyPost?.agent_ref?.principal_agent_first_name}{" "}
                      {propertyPost?.agent_ref?.principal_agent_last_name}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Property</label>
                    <span className="value property-link">
                      <a
                        href={`/properties/view/${propertyPost?.property_id?._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {CommonHelpers.getPropertyAddress({
                          no: propertyPost?.property_id?.property_no,
                          streetName:
                            propertyPost?.property_id
                              ?.property_no_and_street_name,
                          city: propertyPost?.property_id?.city,
                          state: propertyPost?.property_id?.state,
                          postCode: propertyPost?.property_id?.post_code,
                          country: propertyPost?.property_id?.country,
                        })}
                      </a>
                    </span>
                  </div>
                </div>

                <div className="bottom-row">
                  <div className="info-item">
                    <label>Post Type</label>
                    <p>{formatPostType(propertyPost?.post_type)}</p>
                  </div>
                  <div className="info-item">
                    <label>Schedule Datetime</label>
                    <p>
                      {propertyPost?.schedule_datetime
                        ? dayjs(propertyPost.schedule_datetime).format(
                            "MMM DD, YYYY hh:mm A"
                          )
                        : "-"}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Property Stage</label>
                    <p>{formatPostType(propertyPost?.template_label)}</p>
                  </div>
                  <div className="info-item">
                    <label>Created On</label>
                    <p>
                      {dayjs(propertyPost?.createdAt).format(
                        "MMM DD, YYYY hh:mm A"
                      )}
                    </p>
                  </div>
                </div>
                <div className="bottom-row">
                  <div className="info-item">
                    <label>Last Updated On</label>
                    <p>
                      {dayjs(propertyPost?.updatedAt).format(
                        "MMM DD, YYYY hh:mm A"
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* <h2>Property</h2>
              <div className="property-card selected">
                <div className="property-image-wrapper">
                  <img
                    src={imageUrl}
                    alt="Property"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "/images/image-video-generation-error-fallback-image-for-card.png";
                    }}
                  />
                </div>

                <div className="setup-property-tags">
                  {property?.property_type_ref?.name && (
                    <span>{property.property_type_ref.name}</span>
                  )}
                  {property?.listing_type?.name && (
                    <span>{property.listing_type.name}</span>
                  )}
                  {property?.property_stage_ref?.name && (
                    <span>{property.property_stage_ref.name}</span>
                  )}
                </div>

                <p className="setup-address">
                  {[
                    property?.property_no,
                    property?.property_no_and_street_name,
                    property?.city,
                    property?.state,
                    property?.country,
                    property?.post_code,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div> */}

              <div className="post-preview-wrapper-container">
                <div className="configure-post">
                  <h3 className="section-title">Configure Post</h3>
                  <div className="right-actions">
                    <div className="right-actions">
                      <button
                        className="preview"
                        disabled={!hasPendingForUpload}
                        onClick={() => {
                          if (!hasPendingForUpload) return;

                          const designRefs = [
                            propertyPost?.image_post_design_activity_ref,
                            propertyPost?.story_post_design_activity_ref,
                            propertyPost?.video_post_design_activity_ref,
                            propertyPost?.reel_post_design_activity_ref,
                          ].filter(Boolean);

                          const images = designRefs.map((ref) => {
                            const path = ref?.design_task_id?.output_image_path;
                            return path
                              ? CommonHelpers.getImageUrlFromImageTool({ path })
                              : "/images/image-video-generation-error-fallback-image-for-card.png";
                          });

                          setPreviewImages(images);
                          setPreviewIndex(0);
                          setIsPreviewOpen(true);
                        }}
                      >
                        Preview
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className={`post-preview-wrapper ${
                    isExpanded ? "expanded" : ""
                  }`}
                >
                  {hasPendingForUpload ? (
                    <div className="post-preview-card">
                      <div className="post-header">
                        <img
                          src={
                            getImageUrl(
                              propertyPost?.agent_ref?.profile_image_ref
                                ?.compressed_file_path || ""
                            ) || "/images/profile-placeholder-image.png"
                          }
                          alt="Agent"
                          className="avatar"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src =
                              "/images/profile-placeholder-image.png";
                          }}
                        />
                        <div className="user-info">
                          <strong className="name">
                            {propertyPost.agent_ref?.principal_agent_first_name}{" "}
                            {propertyPost.agent_ref?.principal_agent_last_name}
                          </strong>
                          <span className="date">{formattedSchedule}</span>
                        </div>
                      </div>

                      <div className="post-text">
                        <span className="post-content">
                          {isExpanded ? fullText : shortText}
                        </span>
                        <span className="see-more" onClick={toggleExpand}>
                          {isExpanded ? " See less" : " See more"}
                        </span>
                      </div>

                      <div className="carousel-container">
                        {renderDesignImage(
                          [
                            propertyPost?.image_post_design_activity_ref,
                            propertyPost?.story_post_design_activity_ref,
                            propertyPost?.video_post_design_activity_ref,
                            propertyPost?.reel_post_design_activity_ref,
                          ].filter(Boolean)[postPreviewIndex]
                        )}

                        {[
                          propertyPost?.image_post_design_activity_ref,
                          propertyPost?.story_post_design_activity_ref,
                          propertyPost?.video_post_design_activity_ref,
                          propertyPost?.reel_post_design_activity_ref,
                        ].filter(Boolean).length > 1 && (
                          <>
                            <button
                              className="carousel-button left"
                              onClick={() =>
                                setPostPreviewIndex((prev) =>
                                  prev === 0
                                    ? [
                                        propertyPost?.image_post_design_activity_ref,
                                        propertyPost?.story_post_design_activity_ref,
                                        propertyPost?.video_post_design_activity_ref,
                                        propertyPost?.reel_post_design_activity_ref,
                                      ].filter(Boolean).length - 1
                                    : prev - 1
                                )
                              }
                            >
                              <LeftArrowIcon />
                            </button>

                            <button
                              className="carousel-button right"
                              onClick={() =>
                                setPostPreviewIndex((prev) =>
                                  prev ===
                                  [
                                    propertyPost?.image_post_design_activity_ref,
                                    propertyPost?.story_post_design_activity_ref,
                                    propertyPost?.video_post_design_activity_ref,
                                    propertyPost?.reel_post_design_activity_ref,
                                  ].filter(Boolean).length -
                                    1
                                    ? 0
                                    : prev + 1
                                )
                              }
                            >
                              <RightArrowIcon />
                            </button>
                          </>
                        )}
                      </div>

                      {[
                        propertyPost?.image_post_design_activity_ref,
                        propertyPost?.story_post_design_activity_ref,
                        propertyPost?.video_post_design_activity_ref,
                        propertyPost?.reel_post_design_activity_ref,
                      ].filter(Boolean).length > 1 && (
                        <div className="dots-pagination">
                          {[
                            propertyPost?.image_post_design_activity_ref,
                            propertyPost?.story_post_design_activity_ref,
                            propertyPost?.video_post_design_activity_ref,
                            propertyPost?.reel_post_design_activity_ref,
                          ]
                            .filter(Boolean)
                            .map((_, i) => (
                              <span
                                key={i}
                                className={`dot ${
                                  i === postPreviewIndex ? "active" : ""
                                }`}
                                onClick={() => setPostPreviewIndex(i)}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="post-preview-loading">
                      <div className="spinner" />
                      <h4>Your request is being processed.</h4>
                      <p>
                        We’re working on your request! Creating a high-quality
                        output can take a little while. We appreciate your
                        patience.
                      </p>
                      <p className="message">
                        Feel free to wait here, or browse other pages. We’ll
                        send you a notification when your request is completed.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-config">
                <div className="post-text-header">
                  <h4>Post Text</h4>
                  <div className="toggle-same-text">
                    <label className="switch-label">
                      Same text for all the platforms
                      <input
                        type="checkbox"
                        checked={sameTextToggle}
                        onChange={() => setSameTextToggle(!sameTextToggle)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
                <div className="platform-header">
                  <div className="platform-tabs">
                    {tabs.map((tab) => (
                      <div
                        key={tab}
                        className={`platform-tab ${
                          activeTab === tab ? "active" : ""
                        }`}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab}
                      </div>
                    ))}
                  </div>
                </div>

                <textarea
                  className="post-textarea"
                  placeholder="Write your post content here..."
                  defaultValue={""}
                  onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                    const target = e.currentTarget;
                    target.style.height = "auto";
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                />
              </div>
            </>
          )}

          <Modal
            open={processingModalOpen}
            onClose={(event, reason) => {
              if (reason !== "backdropClick") {
                setProcessingModalOpen(false);
              }
            }}
            aria-labelledby="processing-modal"
            className="add-property-modal styled-modal"
            disableEscapeKeyDown
          >
            <div className="modal-center-wrapper">
              <div className="loading-content-wrapper">
                {isProcessing && (
                  <div className="loading-container">
                    <div className="spinner" />
                    <p className="loading-text">
                      Processing your request
                      <span className="desktop-break">
                        <br />
                      </span>
                      Please wait...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Modal>

          {isPreviewOpen && (
            <PreviewModal
              open={isPreviewOpen}
              onClose={() => setIsPreviewOpen(false)}
              images={previewImages}
              initialIndex={previewIndex}
            />
          )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default CoAgentPropertyView;
